import { useEffect, useState } from "react";
import { Loader } from "@/components/common/loader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { Client } from "@/features/clients/types";
import { Form433DEditor } from "@/features/forms/components/form-433d-editor";
import { Form433HEditor } from "@/features/forms/components/form-433h-editor";
import type { FormSubmission } from "@/features/forms/types";
import { getOrCreateCaseSubmission } from "@/features/workflows/api";

type TermsForm = "433-H" | "433-D";

interface Props {
  client: Client;
  caseId: string;
  /** The form chosen at step 6; defaults to 433-H. */
  selectedForm: TermsForm;
  onSelectForm: (form: TermsForm) => void;
}

/**
 * Step 7 — Installment Agreement Terms. Reuses the Tier 3.1 editor for the
 * chosen form: 433-H (combined CIS + IA header) or 433-D (which additionally
 * offers import-from-433-A bank). Both are married-gated on file-as and
 * autosave the terms onto the client's form submission (attached to the case).
 */
interface Loaded {
  form: string;
  submission: FormSubmission | null;
  error: string | null;
}

export function TermsStep({ client, caseId, selectedForm, onSelectForm }: Props) {
  // Track which form the loaded submission is for so `loading` is derived (no
  // synchronous setState reset on form-switch).
  const [loaded, setLoaded] = useState<Loaded>({ form: "", submission: null, error: null });

  useEffect(() => {
    let cancelled = false;
    getOrCreateCaseSubmission(client.id, caseId, selectedForm)
      .then((s) => !cancelled && setLoaded({ form: selectedForm, submission: s, error: null }))
      .catch(
        () =>
          !cancelled &&
          setLoaded({ form: selectedForm, submission: null, error: "Couldn't open the form." }),
      );
    return () => {
      cancelled = true;
    };
  }, [client.id, caseId, selectedForm]);

  const loading = loaded.form !== selectedForm;
  const submission = loading ? null : loaded.submission;
  const error = loaded.error;
  const setSubmission = (s: FormSubmission) =>
    setLoaded((prev) => ({ ...prev, submission: s }));

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">Filing with</span>
        {(["433-H", "433-D"] as const).map((f) => (
          <Button
            key={f}
            size="sm"
            variant={selectedForm === f ? "default" : "outline"}
            onClick={() => onSelectForm(f)}
          >
            Form {f}
          </Button>
        ))}
      </div>

      {loading ? (
        <Loader className="mt-6" />
      ) : error || !submission ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn't open the form</AlertTitle>
          <AlertDescription>{error ?? "Please try again."}</AlertDescription>
        </Alert>
      ) : selectedForm === "433-D" ? (
        <Form433DEditor client={client} submission={submission} onSubmissionChange={setSubmission} />
      ) : (
        <Form433HEditor client={client} submission={submission} onSubmissionChange={setSubmission} />
      )}
    </div>
  );
}
